import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';

/**
 * Destinations page
 * Shows all available shipping destinations with prohibited items information
 */
function Destinations() {
  const [searchTerm, setSearchTerm] = useState('');

  const destinations = ['United States'];

  const filteredDestinations = destinations.filter(dest =>
    dest.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-light min-vh-100">
      <Container className="py-5">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/">Home</Link></li>
            <li className="breadcrumb-item active">Baggage Shipping Destinations</li>
          </ol>
        </nav>

        {/* Header */}
        <div className="text-center mb-5">
          <h1 className="mb-4">Destinations</h1>
          <p className="lead text-muted mb-4">
            No destination around the world is too far for us to deliver your boxes and bags, whether you need shipping to Italy<br />
            for a year studying abroad, as far afield as Cambodia or New Zealand for a Gap Year, or you even just have an<br />
            overweight bag for your quick holiday break to France.
          </p>

          {/* Search Box */}
          <Row className="justify-content-center">
            <Col md={6}>
              <Form.Control
                type="text"
                placeholder="Search for a destination"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="lg"
                className="mb-4"
              />
            </Col>
          </Row>
        </div>

        {/* Destinations List */}
        <Card className="shadow-sm border-0 mb-5">
          <Card.Body className="p-4">
            <Row>
              {(searchTerm ? filteredDestinations : destinations).map((dest, index) => (
                <Col md={3} sm={6} key={index} className="mb-3">
                  <Link to={`/destinations/${dest.toLowerCase().replace(/\s+/g, '-')}`} className="text-decoration-none text-dark">
                    <div className="d-flex align-items-center">
                      <i className="fas fa-map-marker-alt me-2 text-primary"></i>
                      <span>{dest}</span>
                    </div>
                  </Link>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>

        {/* Important Notice */}
        <Alert variant="info" className="mb-5">
          <div className="d-flex align-items-start">
            <i className="fas fa-info-circle me-3 mt-1"></i>
            <div>
              Checking customs information and prohibited items on the destinations linked above is important to ensure your items arrive without issue
            </div>
          </div>
        </Alert>

        {/* Unrivalled Customer Service */}
        <Card className="shadow-sm border-0 mb-5 bg-white">
          <Card.Body className="text-center p-5">
            <div className="mb-3">
              <i className="fas fa-headset fa-3x text-primary"></i>
            </div>
            <h4 className="mb-2">Unrivalled customer service</h4>
            <p className="text-muted mb-3">24/7 Mon-Fri Available Sat & Sun too</p>
            <div className="mb-3">
              <h5 className="text-success mb-2">Done the research?</h5>
              <Link to="/route-prices" className="btn btn-success">
                Check prices and book
              </Link>
            </div>
            <div className="d-flex justify-content-center align-items-center gap-2">
              <div className="text-warning">
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star"></i>
                <i className="fas fa-star-half-alt"></i>
              </div>
              <span className="text-muted">4.7/5 average from 108,000 reviews</span>
            </div>
          </Card.Body>
        </Card>

        {/* Information Section */}
        <section className="mb-5">
          <h3 className="mb-4">Why Ship Your Luggage?</h3>
          <p className="mb-4">
            Travelling abroad can be a stressful time; not only do you need to remember all the tickets, passports and documents you need simply to travel, you 
            frequently also have to deal with lengthy baggage check-in lines, long waits at the luggage collection carousels and the possibility of your bags going missing 
            in transit. On top of all this, airlines are also now penalising the excess of any excess luggage you want to take with you on your trip as well as shrinking the 
            weight allowances you can take. This means that not only do the oversized luggage pieces including golf clubs, skis and surf boards cost you extra, but a bag 
            that's slightly heavier than usual containing filling boots, overweight books or just lots of bulky clothing can be slapped with a fee too.
          </p>
          <p className="mb-4">
            This is where Send My Bag™ can come to the rescue - being able to ship your oversized luggage and heavy bags worldwide at great value, saving you not only 
            on costs, but also on hassle. No distractions around the world is too far for us to deliver your boxes and bags, whether you need shipping to Italy for a year 
            studying abroad, as far afield as Cambodia or New Zealand for a Gap Year, or you even just have an overweight bag for your quick holiday break to France.
          </p>

          <h4 className="mb-3 mt-5">Prepare for the Weather</h4>
          <p className="mb-4">
            If you're taking an extended trip abroad, seeing the sights and getting to grips with the local culture - you'll quite need to 
            take a variety of different clothing with you in order to brave the seasons. For example, arriving in Spain in September will mean you need your swimwear, 
            summerwear and shorts, but heading into Austria in late November will see mountains of snow that you'll need to be protected against with bulky winter coats, 
            thick jumpers and woolly hats. By shipping your luggage with Send My Bag™ you can save yourself the stress of lugging all of this with you as you can take as many 
            excess bags and boxes as you are able to pick up at your destination, whether you need ski boots and skis, or surfboards and sunglasses.
          </p>

          <h4 className="mb-3 mt-5">Customs and Documentation</h4>
          <p className="mb-4">
            Whether you're shipping your luggage within the EU, from the EU to a destination outside of it, or directly to your destination, whether that's within the EU or 
            half way around the world. All you need to do is ensure that you've got the customs forms filled in that we'll provide you with and that you have everything 
            labelled correctly for collection, complete with our labels, to ensure a quick and smooth process.
          </p>
          <p className="mb-4">
            Wondering what your standard airline baggage allowance is? Check out EasyJet's baggage allowance or Ryanair's baggage allowance.
          </p>
        </section>
      </Container>
    </div>
  );
}

export default Destinations;
